import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button, Modal } from '../../components/common/Common';
import Brochure from '../../components/brochure/Brochure';
import { generateBrochurePDF, slugifyFilename } from '../../components/brochure/generatePDF';
import EntityInfoButton from '../../components/common/EntityInfoButton';
import Header from '../../components/layout/Header';
import {
  ArrowLeft, Edit2, Trash2, MapPin, Bed, Bath, Maximize, Building2,
  Share2, Copy, Link as LinkIcon, Eye, XCircle, User, Phone,
  Image as ImageIcon, FileText, Star, X as XIcon, Download, MessageCircle
} from 'lucide-react';
import './ViewProperty.css';

const STATUS_MAP = {
  available: { label: 'Available', color: '#22c55e', bg: '#dcfce7' },
  sold:      { label: 'Sold',      color: '#dc2626', bg: '#fee2e2' },
  rented:    { label: 'Rented',    color: '#2563eb', bg: '#dbeafe' },
  reserved:  { label: 'Reserved',  color: '#d97706', bg: '#fef3c7' },
};

const detectKind = (m) => {
  const t = (m.media_type || '').toLowerCase();
  const url = m.file_url || m.url || '';
  const name = (m.file_name || m.name || '').toLowerCase();
  if (t === 'image' || t === 'floor_plan' || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (t === 'video' || /\.(mp4|webm|mov|avi|mkv)$/i.test(url) || /\.(mp4|webm|mov|avi|mkv)$/i.test(name)) return 'video';
  if (t === 'brochure' || t === 'document' || /\.pdf$/i.test(url) || /\.pdf$/i.test(name)) return 'pdf';
  return 'file';
};

const formatPrice = (price) => {
  if (price == null || price === '') return '-';
  const n = Number(price);
  if (!Number.isFinite(n) || n === 0) return '-';
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + ' Cr';
  if (n >= 100000) return '₹' + (n / 100000).toFixed(2) + ' L';
  return '₹' + n.toLocaleString('en-IN');
};

const ViewProperty = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, isAdmin } = useAuth();
  const canEdit = isAdmin || hasPermission('properties.edit');
  const canDelete = isAdmin || hasPermission('properties.delete');

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const brochureRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`properties/${id}`);
        if (res.success) setProperty(res.data);
      } catch (err) {
        console.error('Fetch property error:', err);
      }
      setLoading(false);
    })();
  }, [id]);

  const handleDelete = async () => {
    try {
      const res = await apiFetch(`properties/${id}`, { method: 'DELETE' });
      if (res.success) navigate('/properties/all');
    } catch (err) {}
    setShowDelete(false);
  };

  const handleShare = async () => {
    setShareLoading(true);
    setShowShareModal(true);
    setCopied(false);
    try {
      const res = await apiFetch('share', {
        method: 'POST',
        body: JSON.stringify({ entity_type: 'property', entity_id: parseInt(id) }),
      });
      if (res.success && res.data) setShareData(res.data);
      else setShowShareModal(false);
    } catch (err) {
      setShowShareModal(false);
    }
    setShareLoading(false);
  };

  const copyShareLink = () => {
    if (shareData?.share_url) {
      navigator.clipboard.writeText(shareData.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const deactivateShareLink = async () => {
    if (!shareData?.link_id) return;
    try {
      const res = await apiFetch(`share/${shareData.link_id}`, { method: 'DELETE' });
      if (res.success) setShareData({ ...shareData, is_active: false });
    } catch (err) {}
  };

  const reactivateShareLink = async (resetCount) => {
    if (!shareData?.link_id) return;
    try {
      const res = await apiFetch(`share/${shareData.link_id}/reactivate`, {
        method: 'PATCH',
        body: JSON.stringify({ reset_count: !!resetCount }),
      });
      if (res.success && res.data) setShareData(res.data);
    } catch (err) {}
  };

  const shareOnWhatsApp = () => {
    if (!shareData?.share_url || !property) return;
    const message = `Check out *${property.title || 'this property'}*${property.location ? ' in ' + property.location : ''}\n\n${shareData.share_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadPDF = async () => {
    if (downloadingPDF || !property) return;
    setDownloadingPDF(true);
    try {
      await new Promise(r => setTimeout(r, 100));
      const filename = slugifyFilename(property.title, 'brochure');
      await generateBrochurePDF(brochureRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed:', err);
    }
    setDownloadingPDF(false);
  };

  if (loading) return <div className="page" style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>;
  if (!property) return <div className="page" style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Property not found</div>;

  const st = STATUS_MAP[property.status] || STATUS_MAP.available;
  const amenities = property.amenities ? property.amenities.split(',').map(a => a.trim()).filter(Boolean) : [];
  const media = Array.isArray(property.media) ? property.media : [];
  const coverImage = media.find(m => m.is_cover && detectKind(m) === 'image');
  const galleryImages = media.filter(m => detectKind(m) === 'image');
  const documents = media.filter(m => detectKind(m) === 'pdf');
  const videos = media.filter(m => detectKind(m) === 'video');

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div>
      <Header title="Property Details" />
      <div className="page">
        {/* Page Hero */}
        <div className="vprop-hero-head">
          <div className="vprop-hero-head__main">
            <div className="vprop-hero-head__title-row">
              <h1 className="vprop-hero-head__title">{property.title}</h1>
              <EntityInfoButton />
            </div>
            {property.location && (
              <p className="vprop-hero-head__subtitle">
                <MapPin size={14} /> {property.location}
              </p>
            )}
            {(property.property_type || property.status) && (
              <div className="vprop-hero-head__badges">
                {property.property_type && (
                  <span className="vprop-hero-head__type">{property.property_type}</span>
                )}
                {property.status && (
                  <span
                    className="vprop-hero-head__status"
                    style={{ color: st.color, background: st.bg }}
                  >
                    {st.label}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="vprop-hero-head__actions">
            <button
              className="vp-hero__btn vp-hero__btn--ghost"
              onClick={() => navigate('/properties/all')}
              title="Back to properties"
            >
              <ArrowLeft size={15} /> <span>Back</span>
            </button>
            {canEdit && (
              <button
                className="vp-hero__btn vp-hero__btn--ghost"
                onClick={() => navigate(`/properties/edit/${id}`)}
                title="Edit property"
              >
                <Edit2 size={15} /> <span>Edit</span>
              </button>
            )}
            <button
              className="vp-hero__btn vp-hero__btn--ghost"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              title="Download brochure"
            >
              <Download size={15} /> <span>{downloadingPDF ? 'Generating...' : 'Download'}</span>
            </button>
            <button
              className="vp-hero__btn vp-hero__btn--primary"
              onClick={handleShare}
              title="Share property"
            >
              <Share2 size={15} /> <span>Share</span>
            </button>
            {canDelete && (
              <button
                className="vp-hero__btn vp-hero__btn--danger"
                onClick={() => setShowDelete(true)}
                title="Delete property"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
        <div className="vprop-layout">
          {/* Left column */}
          <div className="vprop-main">
            {/* Hero — cover image */}
            <div className="vprop-hero">
              {coverImage ? (
                <img
                  src={getFileUrl(coverImage.file_url)}
                  alt={property.title}
                  onClick={() => setLightboxImg(getFileUrl(coverImage.file_url))}
                />
              ) : galleryImages[0] ? (
                <img
                  src={getFileUrl(galleryImages[0].file_url)}
                  alt={property.title}
                  onClick={() => setLightboxImg(getFileUrl(galleryImages[0].file_url))}
                />
              ) : (
                <div className="vprop-hero__placeholder">
                  <ImageIcon size={42} />
                  <span>No images uploaded</span>
                </div>
              )}
              <div className="vprop-hero__overlay">
                <span className="vprop-status-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                {property.property_type && <span className="vprop-type-badge">{property.property_type}</span>}
              </div>
            </div>

            {/* Quick info strip */}
            <div className="vprop-quick">
              {property.bedrooms != null && (
                <div className="vprop-quick__item">
                  <Bed size={16} />
                  <div><strong>{property.bedrooms}</strong> <span>Beds</span></div>
                </div>
              )}
              {property.bathrooms != null && (
                <div className="vprop-quick__item">
                  <Bath size={16} />
                  <div><strong>{property.bathrooms}</strong> <span>Baths</span></div>
                </div>
              )}
              {property.area && (
                <div className="vprop-quick__item">
                  <Maximize size={16} />
                  <div><strong>{property.area}</strong> <span>{property.area_unit || 'sqft'}</span></div>
                </div>
              )}
              {property.configuration && (
                <div className="vprop-quick__item">
                  <Building2 size={16} />
                  <div><strong>{property.configuration}</strong> <span>Config</span></div>
                </div>
              )}
              {property.floor && (
                <div className="vprop-quick__item">
                  <div><strong>Floor {property.floor}</strong> <span>{property.total_floors ? `of ${property.total_floors}` : ''}</span></div>
                </div>
              )}
            </div>

            {/* Basic / Identity */}
            <section className="vprop-section">
              <h3 className="vprop-section__title">Property Information</h3>
              <div className="vprop-grid">
                {[
                  { label: 'Type', value: property.property_type },
                  { label: 'Category', value: property.property_category },
                  { label: 'Property For', value: property.property_for },
                  { label: 'Configuration', value: property.configuration },
                  { label: 'Furnishing', value: property.furnishing },
                  { label: 'Facing', value: property.facing },
                  { label: 'Floor', value: property.floor },
                  { label: 'Total Floors', value: property.total_floors },
                  { label: 'Floor Type', value: property.floor_type },
                  { label: 'Parking', value: property.parking },
                  { label: 'Balconies', value: property.balconies },
                  { label: 'Lift', value: property.has_lift == null ? null : (Number(property.has_lift) ? 'Yes' : 'No') },
                  { label: 'Property Age', value: property.property_age != null && property.property_age !== '' ? `${property.property_age} yr` : null },
                  { label: 'OC Received', value: property.oc_received },
                  { label: 'Unit Number', value: property.unit_number },
                  { label: 'Wing', value: property.wing },
                  { label: 'RERA ID', value: property.rera_id, mono: true },
                  { label: 'Special Tags', value: property.special_tags },
                  { label: 'Keys With', value: property.keys_with },
                  { label: 'Project', value: property.project_name, icon: Building2 },
                  { label: 'Listed On', value: formatDate(property.created_at) },
                ].filter(f => f.value !== null && f.value !== undefined && f.value !== '').map((d, i) => (
                  <div className="vprop-field" key={i}>
                    <span className="vprop-field__label">{d.label}</span>
                    <span className="vprop-field__value" style={d.mono ? { fontFamily: 'monospace', fontSize: 12 } : {}}>
                      {d.icon && React.createElement(d.icon, { size: 13, style: { marginRight: 4, verticalAlign: 'middle' } })}
                      {d.value}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Area breakdown */}
            {(property.carpet_area || property.builtup_area || property.super_builtup_area ||
              property.balcony_area || property.terrace_area || property.garden_area) && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Area Breakdown</h3>
                <div className="vprop-grid vprop-grid--3">
                  {property.carpet_area && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Carpet Area</span>
                      <span className="vprop-field__value vprop-field__value--big">{property.carpet_area} <small>{property.area_unit || 'sqft'}</small></span>
                    </div>
                  )}
                  {property.builtup_area && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Built-up Area</span>
                      <span className="vprop-field__value vprop-field__value--big">{property.builtup_area} <small>{property.area_unit || 'sqft'}</small></span>
                    </div>
                  )}
                  {property.super_builtup_area && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Super Built-up</span>
                      <span className="vprop-field__value vprop-field__value--big">{property.super_builtup_area} <small>{property.area_unit || 'sqft'}</small></span>
                    </div>
                  )}
                  {property.balcony_area && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Balcony Area</span>
                      <span className="vprop-field__value vprop-field__value--big">{property.balcony_area} <small>{property.area_unit || 'sqft'}</small></span>
                    </div>
                  )}
                  {property.terrace_area && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Terrace Area</span>
                      <span className="vprop-field__value vprop-field__value--big">{property.terrace_area} <small>{property.area_unit || 'sqft'}</small></span>
                    </div>
                  )}
                  {property.garden_area && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Garden Area</span>
                      <span className="vprop-field__value vprop-field__value--big">{property.garden_area} <small>{property.area_unit || 'sqft'}</small></span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Pricing */}
            {(property.price || property.base_price || property.price_per_sqft) && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Pricing</h3>
                <div className="vprop-grid vprop-grid--3">
                  {property.price && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Final Price</span>
                      <span className="vprop-field__value vprop-field__value--big" style={{ color: '#16a34a' }}>{formatPrice(property.price)}</span>
                    </div>
                  )}
                  {property.base_price && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Base Price</span>
                      <span className="vprop-field__value vprop-field__value--big">{formatPrice(property.base_price)}</span>
                    </div>
                  )}
                  {property.price_per_sqft && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Per Sqft</span>
                      <span className="vprop-field__value vprop-field__value--big">{formatPrice(property.price_per_sqft)}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Rent & Charges */}
            {(property.rent_amount || property.rent_deposit || property.maintenance_charges) && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Rent &amp; Charges</h3>
                <div className="vprop-grid vprop-grid--3">
                  {property.rent_amount && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Rent Amount</span>
                      <span className="vprop-field__value vprop-field__value--big">{formatPrice(property.rent_amount)}</span>
                    </div>
                  )}
                  {property.rent_deposit && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Deposit</span>
                      <span className="vprop-field__value vprop-field__value--big">{formatPrice(property.rent_deposit)}</span>
                    </div>
                  )}
                  {property.maintenance_charges && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Maintenance</span>
                      <span className="vprop-field__value vprop-field__value--big">{formatPrice(property.maintenance_charges)}{property.maintenance_unit ? <small> /{property.maintenance_unit}</small> : null}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Owner */}
            {(property.owner_type || property.owner_name || property.owner_contact ||
              property.ref_name || property.ref_contact) && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Owner / Source</h3>
                <div className="vprop-grid">
                  {property.owner_type && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Owner Type</span>
                      <span className="vprop-field__value">{property.owner_type}</span>
                    </div>
                  )}
                  {property.owner_name && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Owner Name</span>
                      <span className="vprop-field__value"><User size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />{property.owner_name}</span>
                    </div>
                  )}
                  {property.owner_contact && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Contact</span>
                      <span className="vprop-field__value"><Phone size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />{property.owner_contact}</span>
                    </div>
                  )}
                  {property.ref_name && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Reference Name</span>
                      <span className="vprop-field__value"><User size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />{property.ref_name}</span>
                    </div>
                  )}
                  {property.ref_contact && (
                    <div className="vprop-field">
                      <span className="vprop-field__label">Reference Contact</span>
                      <span className="vprop-field__value"><Phone size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />{property.ref_contact}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Location */}
            {(property.location || property.locality || property.address) && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Location</h3>
                {property.location && (
                  <p className="vprop-location"><MapPin size={14} /> {property.location}{property.locality ? `, ${property.locality}` : ''}</p>
                )}
                {!property.location && property.locality && (
                  <p className="vprop-location"><MapPin size={14} /> {property.locality}</p>
                )}
                {property.address && (
                  <p className="vprop-address">{property.address}</p>
                )}
              </section>
            )}

            {/* Description */}
            {property.description && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Description</h3>
                <p className="vprop-description">{property.description}</p>
              </section>
            )}

            {/* Image gallery */}
            {galleryImages.length > 0 && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Photos <span className="vprop-section__count">({galleryImages.length})</span></h3>
                <div className="vprop-gallery">
                  {galleryImages.map(m => (
                    <div className="vprop-gallery__item" key={m.media_id} onClick={() => setLightboxImg(getFileUrl(m.file_url))}>
                      <img src={getFileUrl(m.file_url)} alt={m.file_name} />
                      {m.is_cover ? <span className="vprop-gallery__cover"><Star size={11} /> Cover</span> : null}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Videos */}
            {videos.length > 0 && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Videos <span className="vprop-section__count">({videos.length})</span></h3>
                <div className="vprop-videos">
                  {videos.map(m => (
                    <div key={m.media_id} className="vprop-video">
                      <video src={getFileUrl(m.file_url)} controls preload="metadata" />
                      <p className="vprop-doc__name">{m.file_name || 'Video'}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Documents */}
            {documents.length > 0 && (
              <section className="vprop-section">
                <h3 className="vprop-section__title">Documents <span className="vprop-section__count">({documents.length})</span></h3>
                <div className="vprop-docs">
                  {documents.map(m => (
                    <a key={m.media_id} href={getFileUrl(m.file_url)} target="_blank" rel="noopener noreferrer" className="vprop-doc">
                      <div className="vprop-doc__icon"><FileText size={22} /></div>
                      <div className="vprop-doc__info">
                        <span className="vprop-doc__name">{m.file_name || 'Document'}</span>
                        <span className="vprop-doc__meta">PDF · Open in new tab</span>
                      </div>
                      <Eye size={16} className="vprop-doc__open" />
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right sidebar */}
          <aside className="vprop-side">
            <div className="vprop-side__price-card">
              <span className="vprop-side__price">{formatPrice(property.price)}</span>
              {property.price_per_sqft ? (
                <span className="vprop-side__per-sqft">{formatPrice(property.price_per_sqft)}/sqft</span>
              ) : null}
              <span className="vprop-side__status" style={{ color: st.color, background: st.bg }}>{st.label}</span>
            </div>

            {amenities.length > 0 && (
              <div className="vprop-side__section">
                <h4 className="vprop-side__title">Amenities</h4>
                <div className="vprop-side__chips">
                  {amenities.map((a, i) => (
                    <span key={i} className="vprop-side__chip">{a}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="vprop-side__section">
              <h4 className="vprop-side__title">Quick Stats</h4>
              <div className="vprop-side__stats">
                <div><span>Images</span><strong>{galleryImages.length}</strong></div>
                <div><span>Documents</span><strong>{documents.length}</strong></div>
                <div><span>Videos</span><strong>{videos.length}</strong></div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="vprop-lightbox" onClick={() => setLightboxImg(null)}>
          <button className="vprop-lightbox__close" onClick={() => setLightboxImg(null)}><XIcon size={20} /></button>
          <img src={lightboxImg} alt="Preview" onClick={e => e.stopPropagation()} />
        </div>
      )}

      <Modal isOpen={showDelete} onClose={() => setShowDelete(false)} title="Delete Property" size="sm">
        <p style={{ color: 'var(--gray-600)', marginBottom: 20 }}>Are you sure?</p>
        <div className="modal__actions">
          <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button variant="danger" icon={Trash2} onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>

      <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} title="Share Property" size="sm">
        {shareLoading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>Generating share link...</div>
        ) : shareData ? (
          shareData.is_active === false ? (
            // ─── Deactivated state ───────────────────────────────────────
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <XCircle size={18} style={{ color: '#dc2626', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>This link is deactivated</div>
                  <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>
                    Visitors will see "Link Unavailable". Reactivate to share again.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
                <Eye size={16} style={{ color: 'var(--gray-400)' }} />
                <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                  <strong style={{ color: 'var(--gray-800)' }}>{shareData.view_count || 0}</strong> previous views
                </span>
              </div>
              <button
                onClick={() => reactivateShareLink(false)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', background: '#16a34a', border: 'none', color: '#ffffff', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 8 }}
              >
                Reactivate (keep {shareData.view_count || 0} views)
              </button>
              <button
                onClick={() => reactivateShareLink(true)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', background: '#ffffff', border: '1px solid #16a34a', color: '#16a34a', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 8 }}
              >
                Reactivate &amp; Reset Count to 0
              </button>
            </div>
          ) : (
            // ─── Active state ────────────────────────────────────────────
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LinkIcon size={16} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                <input
                  readOnly
                  value={shareData.share_url}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 13, background: 'var(--gray-50)', color: 'var(--gray-700)', outline: 'none' }}
                  onClick={e => e.target.select()}
                />
                <Button variant={copied ? 'primary' : 'outline'} icon={Copy} onClick={copyShareLink} style={{ flexShrink: 0 }}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
                <Eye size={16} style={{ color: 'var(--gray-400)' }} />
                <span style={{ fontSize: 13, color: 'var(--gray-600)' }}><strong style={{ color: 'var(--gray-800)' }}>{shareData.view_count || 0}</strong> views</span>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 'auto' }}>Created {formatDate(shareData.created_at)}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button
                  onClick={shareOnWhatsApp}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 14px', background: '#25d366', border: 'none',
                    color: '#ffffff', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', borderRadius: 8,
                  }}
                >
                  <MessageCircle size={16} /> WhatsApp
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={downloadingPDF}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 14px',
                    background: downloadingPDF ? 'var(--gray-200)' : '#0f172a',
                    border: 'none', color: '#ffffff', fontSize: 13, fontWeight: 600,
                    cursor: downloadingPDF ? 'wait' : 'pointer', borderRadius: 8,
                  }}
                >
                  <Download size={16} /> {downloadingPDF ? 'Generating...' : 'Download PDF'}
                </button>
              </div>
              <button onClick={deactivateShareLink} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 0', background: 'none', border: 'none', color: '#ef4444', fontSize: 12, fontWeight: 500, cursor: 'pointer', opacity: 0.8 }}>
                <XCircle size={14} /> Deactivate Link
              </button>
            </div>
          )
        ) : null}
      </Modal>

      {/* Hidden brochure used to render the downloadable PDF */}
      <Brochure
        ref={brochureRef}
        kind="property"
        entity={property}
        resolveImage={getFileUrl}
      />
    </div>
  );
};

export default ViewProperty;
